import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Bell, QrCode } from "lucide-react";
import Seo from "@/components/Seo";

const ComplianceOs = () => {
  return (
    <>
      <Seo
        title="Compliance OS — Client Portal (Coming Soon)"
        description="Calmbreeze Compliance OS: a client-side asset tracker with QR-scannable equipment records, PPM schedules and audit-ready compliance reports. Portal launching soon."
        path="/compliance-os"
      />
      <section className="pt-[160px] pb-24 max-w-editorial mx-auto px-6 md:px-10 lg:px-12 min-h-[70vh]">
        <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-6">
          [ Compliance OS · Client Portal ]
        </div>
        <h1 className="font-display font-bold text-[3rem] md:text-[4.5rem] leading-[0.98] tracking-tightest max-w-4xl">
          The client portal is
          <span className="text-brand"> almost ready.</span>
        </h1>
        <p className="mt-8 text-[18px] text-muted-foreground max-w-2xl leading-relaxed">
          We&rsquo;re building a single view of every asset we look after for you &mdash; QR-scannable
          equipment records, live PPM schedules, and audit-ready compliance reports. Existing clients
          will receive login credentials as soon as your account is onboarded.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="rounded-md border border-black/8 p-6 bg-paper-cool">
            <QrCode className="h-6 w-6 text-brand mb-4" />
            <div className="font-display font-semibold text-[1.05rem] mb-2 tracking-tight">Scan any asset</div>
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              Point your phone at the QR label on any damper, AHU or extract fan &mdash; instantly see
              its full service history and next PPM date.
            </p>
          </div>
          <div className="rounded-md border border-black/8 p-6 bg-paper-cool">
            <Bell className="h-6 w-6 text-brand mb-4" />
            <div className="font-display font-semibold text-[1.05rem] mb-2 tracking-tight">Never miss a PPM</div>
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              Automated reminders for TR/19, fire damper drop-tests, F-Gas checks and TM44 inspections
              &mdash; keyed to your buildings, not a generic calendar.
            </p>
          </div>
          <div className="rounded-md border border-black/8 p-6 bg-paper-cool">
            <div className="h-6 w-6 rounded-sm bg-brand text-white text-[11px] font-mono flex items-center justify-center mb-4">PDF</div>
            <div className="font-display font-semibold text-[1.05rem] mb-2 tracking-tight">Audit-ready reports</div>
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              One-click compliance packs for your insurer, fire risk assessor or FM head office &mdash;
              formatted the way auditors expect them.
            </p>
          </div>
        </div>

        <div className="mt-14 rounded-md bg-ink text-white p-8 md:p-10">
          <div className="font-mono text-[11px] tracking-overline uppercase text-white/60 mb-3">
            [ Launch · Q2 2026 ]
          </div>
          <div className="font-display font-semibold text-[1.5rem] md:text-[1.75rem] tracking-tight mb-4">
            Want early access when your portal goes live?
          </div>
          <p className="text-white/70 text-[15px] leading-relaxed max-w-xl mb-6">
            Email us with your site details and we&rsquo;ll add you to the onboarding queue for
            the first wave of portal access.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="mailto:Hello@calmbreeze.co.uk?subject=Compliance%20OS%20early%20access"
              data-testid="compliance-os-early-access"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-ink font-semibold text-[14px] rounded-sm hover:bg-paper-cool transition-colors"
            >
              Request early access
            </a>
            <Link
              to="/about"
              data-testid="compliance-os-back"
              className="inline-flex items-center gap-2 px-6 py-3.5 border border-white/20 text-white font-semibold text-[14px] rounded-sm hover:bg-white/10 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> Back to About
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ComplianceOs;
